import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Container, Row, Col, Form, Button, Card, Tab, Tabs, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useOutletContext, useParams, useNavigate } from 'react-router-dom';
//import spinnerIcon from './/./../assets/img/loadingSpinner.gif'
import SpinnerComponent from '../components/progressbar/SpinnerComponent.js'
import PaginationComponent from '../components/pagination/PaginationComponent.js'
import { useAuth0 } from "@auth0/auth0-react";
import { apiGet, apiPut, apiPost } from "../services/AuthService.js";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPenToSquare } from '@fortawesome/free-solid-svg-icons'
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons'
import { faArrowUpLong, faArrowDownLong, faSort } from '@fortawesome/free-solid-svg-icons'
import { faFileExcel } from '@fortawesome/free-solid-svg-icons'
import {
    btnGold, boxshadowHover, boxshadowUnHover,
    formLabel, inputSm, goldInputFocus,
    paginationStyle, activePaginationStyle, COLORS, backBtn
} from '../styles/goldTheme.js'

//The Global Mapping account ID (the Account row with IsGlobalMapping = true) varies
//per environment, so we fetch it from the backend at runtime rather than hardcoding.
//The value lives in state and is passed through to populate on first load so the
//initial render doesn't race the async fetch.

const CdexCodesOverride_dc = props => {

    //properties
    const baseUrl = process.env.REACT_APP_API_BASEURL;
    const { getAccessTokenSilently } = useAuth0();

    //stateful properties (hooks)
    const params = useParams();
    const [currentAccount, accountProfile, setAccountProfile, toggleIsShowSidebarMd, setIsShowSidebarMd] = useOutletContext();
    const navigate = useNavigate();

    //owner override view; always uses the signed-in account.
    //Rows start as read-only — clicking the pencil icon enables editing
    //(and persists as an override of the global mapping on save).
    const targetAccountId = currentAccount && currentAccount.id;
    const [pageStatus, setPageStatus] = useState('loading');
    const [dataStatus, setDataStatus] = useState('loading');
    const [pageErrors, setPageErrors] = useState('');
    const [dataErrors, setDataErrors] = useState('');
    const [revLineTypes, setRevLineTypes] = useState([{ code: '', value: '' }, { code: 'R', value: 'REV' }, { code: 'T', value: 'TAX' }, { code: 'D', value: 'DED' }, { code: 'N', value: 'NET' }]);
    const [originalProductCodes, setOriginalProductCodes] = useState([]);
    const [productCodes, setProductCodes] = useState([]);
    const [selectedProductIndex, setSelectedProductIndex] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [anyProductDirty, setAnyProductDirty] = useState(false);
    const [anyTransactionDirty, setAnyTransactionDirty] = useState(false);
    const [isReadOnly, setIsReadOnly] = useState(true);
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const [hoveredTransactionIndex, setHoveredTransactionIndex] = useState(null);
    //the Account row flagged IsGlobalMapping = true. Loaded from the backend on mount.
    const [globalAccountId, setGlobalAccountId] = useState(null);
    const [dataCriteria, setDataCriteria] = useState({
        skipCount: 0,
        takeCount: 25
    });
    const [rowCount, setRowCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const companyTopRef = useRef(null);
    const editRef = useRef(null);

    // Filter / sort state for the master pane (Products list).
    // Sort cycle: column not active → asc → desc → off. When a different column
    // is clicked, the new column starts at asc.
    const [productDescFilter, setProductDescFilter] = useState('');
    const [cdexProductCodeFilter, setCdexProductCodeFilter] = useState('');
    const [productSortBy, setProductSortBy] = useState(null);   // 'productDesc' | 'cdexProductCode' | null
    const [productSortDir, setProductSortDir] = useState(null); // 'asc' | 'desc' | null

    // Filter / sort state for the detail pane (Transactions list for the selected product).
    const [transactionDescFilter, setTransactionDescFilter] = useState('');
    const [cdexTransactionCodeFilter, setCdexTransactionCodeFilter] = useState('');
    const [revLineTypeFilter, setRevLineTypeFilter] = useState('');
    const [transactionSortBy, setTransactionSortBy] = useState(null);   // 'transactionDesc' | 'cdexTransactionCode' | 'revLineTypeCode' | null
    const [transactionSortDir, setTransactionSortDir] = useState(null); // 'asc' | 'desc' | null

    // XLS download status — mirrors the admin dashboard's downloadDataStatus
    // ('idle' | 'loading' | 'ready' | 'error'). Drives the button → spinner swap
    // while the file is being generated.
    const [downloadDataStatus, setDownloadDataStatus] = useState('idle');

    //initialize page — fetch the global mapping account id first, then populate
    useEffect(() => {
        (async () => {
            if (!props.embedded && setIsShowSidebarMd)
                setIsShowSidebarMd(true);

            const init = await initializeForm();
            if (!init.success) {
                setPageStatus('error');
                setPageErrors(init.error || 'Failed to initialize page.');
                return;
            }
            try {
                //pass the freshly-fetched id directly so we don't race React's state update
                await populateCdexProductCodes(1, init.globalAccountId);
                setPageStatus('ready');
            } catch (ex) {
                setPageStatus('error');
                setPageErrors(ex.message);
                setDataStatus('error');
                setDataErrors(ex.message);
            }
        })();
    }, []);

    //initialize form — fetch the global mapping account id from the backend
    const initializeForm = async () => {
        try {
            const response = await apiGet('accounts/GlobalMapping/Id', getAccessTokenSilently);
            if (!response.ok) {
                return { success: false, error: 'Failed to fetch global mapping account ID.' };
            }
            const id = await response.json();
            if (!id) {
                return { success: false, error: 'No global mapping account configured for this environment.' };
            }
            setGlobalAccountId(id);
            return { success: true, globalAccountId: id };
        } catch (ex) {
            return { success: false, error: ex.message || 'Error fetching global mapping account ID.' };
        }
    };

    //populate productCodes
    //
    //Override page merges two API responses:
    //  - Global mappings from the TOTData Global Mapping account (master list)
    //  - Current account's existing overrides
    //
    //For each global row:
    //  - if current account has a row with a matching productDesc, that's a pre-existing
    //    override: use the current account's cdexProductCode value, mark the row as
    //    editable (isReadOnly = false), no pencil needed
    //  - if no match, the row inherits global values: mark read-only with pencil so the
    //    user can click to start overriding it
    //Same comparison runs per-transaction inside each product (transactionDesc).
    //
    //explicitGlobalId (optional): pass the global mapping account id directly to skip
    //the state read — needed on first load because the state hasn't propagated yet
    //after the just-completed initializeForm fetch. Subsequent calls (e.g. pagination)
    //fall back to the state value.
    const populateCdexProductCodes = async (currentPage, explicitGlobalId) => {
        //note: have to recalcaulate skipCount here since state updates aren't active until re-render
        let skipCount = (currentPage === 1 ? 0 : (currentPage - 1) * dataCriteria.takeCount);

        const currentCriteria = {
            skipCount: skipCount,
            takeCount: dataCriteria.takeCount
        };

        const globalId = explicitGlobalId !== undefined ? explicitGlobalId : globalAccountId;
        if (!globalId) {
            setDataStatus('error');
            setDataErrors('Global mapping account ID is not available yet.');
            return;
        }

        try {
            //fire both calls in parallel
            const [globalResponse, currentResponse] = await Promise.all([
                getCdexProductCodes(currentCriteria, globalId),
                getCdexProductCodes(currentCriteria, targetAccountId)
            ]);

            if (!globalResponse.data || globalResponse.data.length === 0) {
                setHoveredIndex(null);
                setSelectedProductIndex(null);
                setSelectedProduct(null);
                setAnyProductDirty(false);
                setAnyTransactionDirty(false);
                setDataStatus('nodata');
                return;
            }

            //index current account's products by lowercased productDesc for O(1) lookup
            const currentByDesc = new Map();
            (currentResponse.data || []).forEach(p => {
                if (p && p.productDesc) {
                    currentByDesc.set(p.productDesc.toLowerCase(), p);
                }
            });

            const mergedData = globalResponse.data.map(globalRow => {
                const override = globalRow.productDesc
                    ? currentByDesc.get(globalRow.productDesc.toLowerCase())
                    : null;

                //index the override's transactions (if any) by transactionDesc
                const overrideTxByDesc = new Map();
                if (override && override.cdexTransactionRowDTO && override.cdexTransactionRowDTO.data) {
                    override.cdexTransactionRowDTO.data.forEach(t => {
                        if (t && t.transactionDesc) {
                            overrideTxByDesc.set(t.transactionDesc.toLowerCase(), t);
                        }
                    });
                }

                const mergedTransactions = (globalRow.cdexTransactionRowDTO && globalRow.cdexTransactionRowDTO.data)
                    ? globalRow.cdexTransactionRowDTO.data.map(globalT => {
                        const overrideT = globalT.transactionDesc
                            ? overrideTxByDesc.get(globalT.transactionDesc.toLowerCase())
                            : null;
                        if (overrideT) {
                            //match → editable, no pencil; carry the override record's id so
                            //save targets the override row, not the global row
                            return {
                                ...globalT,
                                id: overrideT.id,
                                cdexTransactionCode: overrideT.cdexTransactionCode,
                                revLineTypeCode: overrideT.revLineTypeCode,
                                isReadOnly: false
                            };
                        }
                        //no match → read-only with pencil
                        return { ...globalT, isReadOnly: true };
                    })
                    : [];

                if (override) {
                    //match → editable, no pencil; carry over the override's cdexProductCode
                    //and the override record's id so save targets the override row, not global
                    return {
                        ...globalRow,
                        id: override.id,
                        cdexProductCode: override.cdexProductCode,
                        isReadOnly: false,
                        cdexTransactionRowDTO: {
                            ...globalRow.cdexTransactionRowDTO,
                            data: mergedTransactions
                        }
                    };
                }
                //no match → read-only with pencil
                return {
                    ...globalRow,
                    isReadOnly: true,
                    cdexTransactionRowDTO: {
                        ...globalRow.cdexTransactionRowDTO,
                        data: mergedTransactions
                    }
                };
            });

            setIsReadOnly(true);
            setHoveredIndex(null);
            setSelectedProductIndex(0);
            setSelectedProduct(mergedData[0]);
            setAnyProductDirty(false);
            setAnyTransactionDirty(false);

            setRowCount(globalResponse.rowCount);
            setOriginalProductCodes(mergedData);
            setProductCodes(mergedData);
            setDataStatus('ready');
        } catch (ex) {
            setDataStatus('error');
            setDataErrors(ex.message);
        }
    };

    //get productCodes for a given accountId (used to load both the global mappings
    //and the current account's overrides in parallel)
    const getCdexProductCodes = async (criteria, accountId) => {
        //console.log('Getting CdexProductCodes...');

        const apiUrl = 'accounts/' + accountId + '/cdexproductcodes?skipcount=' + criteria.skipCount + '&takecount=' + criteria.takeCount + '&documentType=DC_REV';
        //console.log(apiUrl);

        const response = await apiGet(apiUrl, getAccessTokenSilently);
        const json = await response.json();
        console.log('Response - Status: ' + response.status + ', Success: ' + response.ok + ', Json: ' + JSON.stringify(json));

        return { success: response.ok, rowCount: json.rowCount, data: json.data };
    };

    //handle product changes
    const handleProductChange = (event) => {
        event.preventDefault();

        const fieldName = event.target.name;
        let value = event.target.value;
        //console.log('ownerId: ' + ownerId, 'Field: ' + fieldName + ', Value: ' + value);

        if (fieldName == 'cdexProductCode') {
            if (value.length > 2) {
                value = value.substring(0, 2);
            }
        }

        let anyProductDirty = false;
        let anyProductInvalid = false;
        const newProductList = [];
        productCodes.map((product, index) => {
            if (index === selectedProductIndex) {
                const originalProduct = originalProductCodes[index];
                const originalValue = originalProduct.cdexProductCode;

                const isDirty = (value !== originalValue ? true : false);
                const isValid = (value !== '' ? true : false);

                newProductList.push({
                    ...product,
                    [fieldName]: value,
                    isDirty: isDirty,
                    isValid: isValid
                });

                if (isDirty && isValid)
                    anyProductDirty = true;

                if (isDirty && !isValid)
                    anyProductInvalid = true;

                //console.log('Updating product: ' + product.productCode);
            }
            else {
                newProductList.push(product);

                if (product.isDirty && product.isValid)
                    anyProductDirty = true;

                if (product.isDirty && !product.isValid)
                    anyProductInvalid = true;
            }            
        });
        setProductCodes(newProductList);

        setAnyProductDirty(anyProductDirty && !anyProductInvalid);

        newProductList.map(product => {
            console.log('New Product: ' + product.productCode + ', ' + product.productDesc + ', ' + product.cdexProductCode + ', ' + product.isDirty);
        });
    }

    //handle transaction changes
    const handleTransactionChange = (currentIndex, event) => {
        event.preventDefault();

        const fieldName = event.target.name;
        let value = event.target.value;
        //console.log('Current Index: ' + currentIndex + ', Field: ' + fieldName + ', Value: ' + value);

        if (fieldName == 'cdexTransactionCode') {
            if (value.length > 3) {
                value = value.substring(0, 3);
            }
        }

        let anyTransactionDirty = false;
        let anyTransactionInvalid = false;        
        const newProductList = [];
        const newTransactionList = [];
        productCodes.map((product, productIndex) => {
            const originalProduct = originalProductCodes[productIndex];

            if (productIndex === selectedProductIndex) {

                product.cdexTransactionRowDTO.data.map((transaction, transactionIndex) => {

                    if (transactionIndex === currentIndex) {
                        const originalTransaction = originalProduct.cdexTransactionRowDTO.data[transactionIndex];
                        const originalCdexTranCode = originalTransaction.cdexTransactionCode;
                        const originalRevLineTypeCode = originalTransaction.revLineTypeCode;

                        const cdexTranCodeValue = fieldName === 'cdexTransactionCode' ? value : transaction.cdexTransactionCode;
                        const revLineTypeCode = fieldName === 'revLineTypeCode' ? value : transaction.revLineTypeCode;
                        const isDirty = (cdexTranCodeValue !== originalCdexTranCode || revLineTypeCode !== originalRevLineTypeCode);
                        const isValid = (cdexTranCodeValue !== '' && revLineTypeCode !== '') || (cdexTranCodeValue === '' && revLineTypeCode === '');

                        newTransactionList.push({
                            ...transaction,
                            [fieldName]: value,
                            isDirty: isDirty,
                            isValid: isValid
                        });

                        if (isDirty && isValid)
                            anyTransactionDirty = true;

                        if (isDirty && !isValid)
                            anyTransactionInvalid = true;
                    }
                    else {
                        newTransactionList.push(transaction);

                        if (transaction.isDirty && transaction.isValid)
                            anyTransactionDirty = true;

                        if (transaction.isDirty && !transaction.isValid)
                            anyTransactionInvalid = true;
                    }
                });

                newProductList.push({
                    ...product,
                    cdexTransactionRowDTO: { ...product.cdexTransactionRowDTO, data: newTransactionList }
                });

                //console.log('Updating transaction: ' + transaction.transactionCode);
            }
            else {
                newProductList.push(product);
            }
        });
        setProductCodes(newProductList);
        setSelectedProduct(newProductList[selectedProductIndex]);
        setAnyTransactionDirty(anyTransactionDirty && !anyTransactionInvalid);

        newProductList.map((product, productIndex) => {
            console.log('New Product: ' + product.productCode + ', ' + product.productDesc + ', ' + product.cdexProductCode + ', ' + product.isDirty);

            if (productIndex === selectedProductIndex) {
                product.cdexTransactionRowDTO.data.map(transaction => {
                    console.log('New Transaction: ' + transaction.transactionCode + ', ' + transaction.transactionDesc + ', ' + transaction.cdexTransactionCode + ', ' + transaction.revLineTypeCode + ', ' + transaction.isDirty + ', ' + transaction.isValid);
                });
            }
        });
    }

    // Toggle the "apply to all products" flag on a single transaction row in the
    // currently-selected product. This is a save-time directive only — it does NOT
    // affect dirtiness or validity on its own. When the user clicks "Map Transaction
    // Codes", any dirty transaction with applyToAll === true gets its new values
    // propagated to every other product that has a transaction with the same
    // TransactionDesc, and those products get saved too.
    const handleTransactionApplyToAllChange = (currentIndex, checked) => {
        const newProductList = productCodes.map((product, productIndex) => {
            if (productIndex !== selectedProductIndex) return product;
            const newTransactions = product.cdexTransactionRowDTO.data.map((t, i) =>
                i === currentIndex ? { ...t, applyToAll: checked } : t
            );
            return {
                ...product,
                cdexTransactionRowDTO: { ...product.cdexTransactionRowDTO, data: newTransactions }
            };
        });
        setProductCodes(newProductList);
        setSelectedProduct(newProductList[selectedProductIndex]);
    };

    //update products
    const updateProducts = async () => {
        console.log('Updating Products...');
        setDataStatus('loading');

        //disable Map buttons while mapping
        const tranDirtyState = anyTransactionDirty;
        setAnyTransactionDirty(false);
        setAnyProductDirty(false);

        //console.log('Id: ' + selectedProduct.id + ', Product Code: ' + selectedProduct.productCode);

        await saveProducts()
            .then(response => {
                if (response.success) {
                    setAnyProductDirty(false);
                    setOriginalProductCodes(response.data);
                    setProductCodes(response.data);
                    setSelectedProduct(response.data[selectedProductIndex]);
                    setAnyTransactionDirty(tranDirtyState);
                    setDataStatus('ready')
                }
                else {
                    //TODO: figure out what to do here
                    console.log('nodata');
                    //setDataStatus('nodata')
                }
            })
            .catch(ex => {
                setDataStatus('error');
                setDataErrors(ex.message);
            })
            .finally(response => {
                //do something
            })
    };

    //save products
    const saveProducts = async () => {
        //console.log('Saving Products...');

        const apiUrl = 'accounts/' + targetAccountId + '/OverrideProductCodes?documentType=DC_REV';
        const jsonBody = JSON.stringify(productCodes);
        //console.log(apiUrl);
        //console.log(jsonBody);

        const response = await apiPut(apiUrl, jsonBody, getAccessTokenSilently);
        const json = await response.json();

        return { success: response.ok, data: json };
    };

    //update transaction
    // Propagate any applyToAll changes from the selectedProduct's transactions
    // out to every other product that has a transaction with a matching
    // TransactionDesc. Returns a new productCodes array — caller decides whether
    // to commit it to state. Pure / no side effects, so it's easy to chain into
    // the save flow.
    //
    // Matching transactions in other products get the new cdexTransactionCode /
    // revLineTypeCode, isDirty=true (so they save), and isReadOnly=false. The
    // parent product is also flipped to !isReadOnly so the FK target exists
    // (same cascade rule the pencil toggle uses).
    const applyAllPropagation = (products) => {
        const selProd = products[selectedProductIndex];
        if (!selProd || !selProd.cdexTransactionRowDTO || !selProd.cdexTransactionRowDTO.data) {
            return products;
        }

        // Collect (transactionDesc → new values) for every applyToAll && isDirty
        // transaction in the selected product.
        const propagations = new Map();
        selProd.cdexTransactionRowDTO.data.forEach(t => {
            if (t.applyToAll && t.isDirty && t.transactionDesc) {
                propagations.set(t.transactionDesc.toLowerCase(), {
                    cdexTransactionCode: t.cdexTransactionCode,
                    revLineTypeCode: t.revLineTypeCode
                });
            }
        });

        if (propagations.size === 0) return products;

        return products.map((product, productIndex) => {
            if (productIndex === selectedProductIndex) return product;
            if (!product.cdexTransactionRowDTO || !product.cdexTransactionRowDTO.data) return product;

            let productChanged = false;
            const newTransactions = product.cdexTransactionRowDTO.data.map(t => {
                const desc = (t.transactionDesc || '').toLowerCase();
                const prop = propagations.get(desc);
                if (!prop) return t;

                //Skip if the value is already what we'd propagate to (avoid spurious dirty flag)
                if (t.cdexTransactionCode === prop.cdexTransactionCode && t.revLineTypeCode === prop.revLineTypeCode) {
                    return t;
                }

                productChanged = true;
                return {
                    ...t,
                    cdexTransactionCode: prop.cdexTransactionCode,
                    revLineTypeCode: prop.revLineTypeCode,
                    isDirty: true,
                    isValid: true,
                    isReadOnly: false
                };
            });

            if (!productChanged) return product;

            return {
                ...product,
                //cascade rule: if we flipped a transaction off readonly here, the parent
                //product must also be marked non-readonly so the override row gets created
                isReadOnly: false,
                cdexTransactionRowDTO: { ...product.cdexTransactionRowDTO, data: newTransactions }
            };
        });
    };

    // Build the list of flat export rows from the CURRENT filtered/sorted view.
    // Walks visibleProducts (which already has master-pane filters + sort applied)
    // and, for each product, applies the same detail-pane filters + sort that the
    // user has set on the right side. The result is exactly what the user sees if
    // they were to click through every product on screen.
    //
    // A product with no matching transactions still emits a single row so the
    // export shows that the product exists with its CDEX code; this matches the
    // empty-state behavior in showDataEdit.
    const buildExportRows = () => {
        const rows = [];

        visibleProducts.forEach(product => {
            //per-product transaction filtering (mirrors visibleTransactions logic,
            //which is only computed for the SELECTED product; we need it for all
            //visible products here)
            const allTx = (product.cdexTransactionRowDTO && product.cdexTransactionRowDTO.data) || [];
            let visibleTx = allTx;
            if (transactionDescFilter) {
                const q = transactionDescFilter.toLowerCase();
                visibleTx = visibleTx.filter(t => (t.transactionDesc || '').toLowerCase().includes(q));
            }
            if (cdexTransactionCodeFilter !== '') {
                visibleTx = visibleTx.filter(t => (t.cdexTransactionCode || '') === cdexTransactionCodeFilter);
            }
            if (revLineTypeFilter !== '') {
                visibleTx = visibleTx.filter(t => (t.revLineTypeCode || '') === revLineTypeFilter);
            }
            if (transactionSortBy && transactionSortDir) {
                visibleTx = [...visibleTx].sort((a, b) => {
                    const r = cmpStr(a[transactionSortBy], b[transactionSortBy]);
                    return transactionSortDir === 'asc' ? r : -r;
                });
            }

            const productSource = product.isReadOnly === false ? 'Override' : 'GLOBAL';

            if (visibleTx.length === 0) {
                //When a transaction filter is active, a product with no matching
                //transactions shouldn't appear at all — emitting an empty-transaction
                //row would just leave blank columns in the export. Only emit the
                //"product exists but has no transactions" fallback when there's no
                //transaction filter applied.
                const anyTxFilterActive =
                    !!transactionDescFilter
                    || cdexTransactionCodeFilter !== ''
                    || revLineTypeFilter !== '';
                if (anyTxFilterActive) return;

                rows.push({
                    productDesc: product.productDesc || '',
                    cdexProductCode: product.cdexProductCode || '',
                    transactionDesc: '',
                    cdexTransactionCode: '',
                    revLineTypeCode: '',
                    source: productSource
                });
                return;
            }

            visibleTx.forEach(t => {
                //Source is "Override" if either the parent product OR this specific
                //transaction is overridden (isReadOnly === false flag set during the
                //merge in populateCdexProductCodes)
                const txSource = (product.isReadOnly === false || t.isReadOnly === false) ? 'Override' : 'GLOBAL';
                rows.push({
                    productDesc: product.productDesc || '',
                    cdexProductCode: product.cdexProductCode || '',
                    transactionDesc: t.transactionDesc || '',
                    cdexTransactionCode: t.cdexTransactionCode || '',
                    //Send the human-friendly label (REV/TAX/DED/NET) instead of the raw
                    //single-letter code so the Excel matches what the UI shows.
                    revLineTypeCode: revLineTypeLabel(t.revLineTypeCode) || (t.revLineTypeCode || ''),
                    source: txSource
                });
            });
        });

        return rows;
    };

    // Download the CDEX Override view as XLSX. POSTs the rows we currently have on
    // screen (filtered + sorted) to /accounts/{id}/cdexoverrideexcel — the backend
    // just formats them as a workbook and streams the .xlsx back, no re-querying.
    const handleExportXls = async () => {
        if (!targetAccountId) return;
        setDownloadDataStatus('loading');
        try {
            const exportRows = buildExportRows();
            if (exportRows.length === 0) {
                setDownloadDataStatus('idle');
                console.log('No rows match the current filters — nothing to export.');
                return;
            }

            const apiUrl = 'accounts/' + targetAccountId + '/cdexoverrideexcel';
            const fetchResponse = await apiPost(apiUrl, JSON.stringify(exportRows), true, getAccessTokenSilently);
            if (!fetchResponse.ok) {
                throw new Error('XLS export failed (HTTP ' + fetchResponse.status + ').');
            }
            const blob = await fetchResponse.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'CDEXAccountOverride.xlsx');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            setDownloadDataStatus('ready');
        } catch (err) {
            console.error('Error exporting CDEX Override XLSX:', err);
            setDownloadDataStatus('error');
        }
    };

    const updateTransactions = async () => {
        console.log('Updating Transactions...');
        setDataStatus('loading');

        //disable Map buttons while mapping
        const prodDirtyState = anyProductDirty;
        setAnyTransactionDirty(false);
        setAnyProductDirty(false);

        //Step 1: propagate any applyToAll changes across other products before save
        const propagatedProducts = applyAllPropagation(productCodes);

        //Step 2: build the list of products that actually need saving (have any
        //isDirty transaction). This is always the selectedProduct, plus any other
        //product that received a propagated change.
        const productsToSave = propagatedProducts
            .map((p, idx) => ({ product: p, index: idx }))
            .filter(({ product }) =>
                product.cdexTransactionRowDTO
                && product.cdexTransactionRowDTO.data
                && product.cdexTransactionRowDTO.data.some(t => t.isDirty)
            );

        if (productsToSave.length === 0) {
            //nothing dirty — should not happen because the Map button is disabled,
            //but bail out gracefully just in case
            setAnyTransactionDirty(false);
            setAnyProductDirty(prodDirtyState);
            setDataStatus('ready');
            return;
        }

        try {
            //Step 3: save ALL affected products in a single bulk API call. The
            //backend wraps the operation in one DB transaction so either every
            //product persists or none do (no partial-success states to recover from).
            const response = await saveProductsBulk(productsToSave.map(({ product }) => product));
            if (!response.success) {
                throw new Error('Bulk save failed.');
            }

            //Step 4: merge the saved DTOs back into productCodes by their original
            //index so order/positioning stays exactly the same.
            const newProductList = [...propagatedProducts];
            const savedData = Array.isArray(response.data) ? response.data : [];
            productsToSave.forEach(({ index }, i) => {
                if (savedData[i]) newProductList[index] = savedData[i];
            });

            setOriginalProductCodes(newProductList);
            setProductCodes(newProductList);
            setSelectedProduct(newProductList[selectedProductIndex]);
            setAnyProductDirty(prodDirtyState);
            setAnyTransactionDirty(false);
            setDataStatus('ready');
        } catch (ex) {
            console.log('Error during transactions save: ' + ex.message);
            setDataStatus('error');
            setDataErrors(ex.message);
        }
    };

    //Bulk-save a list of products. The backend wraps the work in a single DB
    //transaction so the whole fan-out (selectedProduct + every other product
    //that received an apply-to-all propagation) commits atomically.
    const saveProductsBulk = async (products) => {
        console.log('Bulk-saving ' + (products?.length ?? 0) + ' product(s)');

        const apiUrl = 'accounts/' + targetAccountId + '/overridetransactioncodesbulk?documentType=DC_REV';
        const jsonBody = JSON.stringify(products);

        const response = await apiPut(apiUrl, jsonBody, getAccessTokenSilently);
        const json = await response.json();

        return { success: response.ok, data: json };
    };

    //save transactions (legacy wrapper — kept in case anything else calls it; routes
    //through the new bulk endpoint with a single-element array so the contract stays
    //the same from the caller's perspective)
    const saveTransactions = async () => {
        const response = await saveProductsBulk([selectedProduct]);
        return {
            success: response.success,
            data: Array.isArray(response.data) && response.data.length > 0 ? response.data[0] : null
        };
    };

    //pencil toggle for a product row — flips isReadOnly so the input becomes editable
    const togglePencilProduct = (index) => {
        const newProductList = productCodes.map((product, i) =>
            i === index ? { ...product, isReadOnly: !product.isReadOnly } : product
        );
        setProductCodes(newProductList);
        if (index === selectedProductIndex) {
            setSelectedProduct(newProductList[index]);
        }
    };

    //pencil toggle for a transaction row inside the currently-selected product
    //
    //Cascade rule: a CDEXProductTransactionCode row has a FK to AccountCDEXProductCode.
    //If the owner doesn't yet have an override row for the parent product, the transaction
    //has nothing to attach to. So when the user opens a transaction for override here, we
    //also flip the parent product to overridden — its cdexProductCode value stays the same
    //as the global default until the user explicitly edits it.
    const togglePencilTransaction = (index) => {
        const newProductList = productCodes.map((product, productIndex) => {
            if (productIndex !== selectedProductIndex) return product;
            const transactionWasReadOnly = product.cdexTransactionRowDTO.data[index]
                && product.cdexTransactionRowDTO.data[index].isReadOnly;
            const newTransactions = product.cdexTransactionRowDTO.data.map((t, i) =>
                i === index ? { ...t, isReadOnly: !t.isReadOnly } : t
            );
            return {
                ...product,
                //cascade: if we just opened a transaction for override, ensure the parent
                //product is also marked as overridden so the FK target exists on save
                isReadOnly: transactionWasReadOnly ? false : product.isReadOnly,
                cdexTransactionRowDTO: { ...product.cdexTransactionRowDTO, data: newTransactions }
            };
        });
        setProductCodes(newProductList);
        setSelectedProduct(newProductList[selectedProductIndex]);
    };

    //handle mouse over
    const handleMouseOver = async (index) => {
        console.log('Mouse Over: ' + index);

        if (index != selectedProductIndex) {
            setHoveredIndex(index);
        }
    };

    //handle mouse leave
    const handleMouseLeave = async (index) => {
        console.log('Mouse Leave: ' + index);

        if (index != selectedProductIndex) {
            setHoveredIndex(null);
        }
    };

    //handle mouse click
    const handleMouseClick = async (index) => {
        console.log('Mouse Click: ' + index);

        setHoveredIndex(null);
        setSelectedProductIndex(index);
        setSelectedProduct(productCodes[index]);

        let anyTransactionDirty = false;
        let anyTransactionInvalid = false;
        productCodes[index].cdexTransactionRowDTO.data.map(transaction => {
            if (transaction.isDirty && transaction.isValid) {
                anyTransactionDirty = true;
            }

            if (transaction.isDirty && !transaction.isValid) {
                anyTransactionInvalid = true;
            }
        });
        setAnyTransactionDirty(anyTransactionDirty && !anyTransactionInvalid);
    };

    //handle page click
    const handlePageClick = async (currentPage) => {
        //console.log('Current Page: ' + currentPage);

        //setDataStatus('loading');

        setDataCriteria({
            ...dataCriteria,
            skipCount: 0
        });

        //don't need to do here, since pagination component sets before calling this method
        //setCurrentPage(1);

        await populateCdexProductCodes(currentPage);
    };

    //render page loading
    //note: spinner overlay will eventually be at the page level (I think)
    const showPageLoading = () => {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                <Spinner animation="border" style={{ color: COLORS.gold }} />
            </div>
        );
    }

    //render page loading
    const showDataLoading = () => {
        return (
            <Row style={{ justifyContent: 'center', padding: '40px 0' }}>
                <Spinner animation="border" style={{ color: COLORS.gold, width: 'auto' }} />
            </Row>
        );
    }

    //render page error
    const showPageError = () => {
        return (
            <div>
                Oops, there was a page load error: {pageErrors}
            </div>
        );
    }

    //render data error
    const showDataError = () => {
        return (
            <Row>
                Oops, there was a data load error: {dataErrors}
            </Row>
        );
    }

    //render table container
    const showTableContainer = () => {
        return (
            <Card className="mb-4" style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 14px rgba(0,0,0,.10)' }}>
                <Card.Header style={{ background: '#333333', color: '#f1f1f1', borderBottom: 'none', padding: props.embedded ? '8px 12px' : '12px 16px' }}>
                    {showInputCriteria()}
                </Card.Header>
                <Card.Body style={{paddingLeft: '12px', paddingRight: '10px'}} >
                    {(() => {
                        switch (dataStatus) {
                            case 'nodata':
                                return <p>No Data Loaded.</p>
                            case 'loading':
                                return showDataLoading()
                            case 'ready':
                                return showData()
                            case 'error':
                                return showDataError()
                            default:
                                return <Row>No Data Loaded.</Row>
                        }
                    })()}
                </Card.Body>
            </Card>
        );
    }

    //render productCodes
    const showData = () => {
        return (
            <Row>
                {/* Note: no `overflow: auto` on either Col — the inner <ul>'s
                    now have their own overflowY: auto (added so the sticky
                    filter row stays pinned at the top of the scrollable list).
                    Keeping it on the Cols too produced double scrollbars (a
                    redundant "extra sidebar" on the right edge). The Col still
                    bounds the height of the detail pane; scrolling happens
                    one level deeper inside the ul. */}
                <Col xs={5} style={{ marginTop: '-16px', marginBottom: '-16px', paddingRight: '0px', paddingLeft: '0px', borderRight: '1px solid #e5e7eb' }} >
                {(() => {
                    switch (dataStatus) {
                        case 'ready':
                            return showDataMaster()
                        default:
                            return null
                    }
                    })()}
                </Col>
                <Col xs={7} style={{ height: props.embedded ? '405px' : '465px', paddingLeft: '20px', marginTop: '-10px', marginBottom: '-16px' }}>
                    {(() => {
                        switch (dataStatus) {
                            case 'ready':
                                return showDataDetail()
                            default:
                                return null
                        }
                    })()}
                </Col>
            </Row>
        );
    }

    //----------------------------------------------------------------
    // Filter / sort helpers for the master (products) and detail (transactions) panes.
    // All operate on the in-memory dataset (backend already loads the whole list per
    // account+documentType), so "whole dataset" filtering is satisfied client-side.
    //----------------------------------------------------------------

    // Sort cycle: not active → asc → desc → off. Clicking a different column resets to asc.
    // Used by columns whose natural order isn't already meaningful (the CDEX code dropdowns).
    const cycleProductSort = (column) => {
        if (productSortBy !== column) {
            setProductSortBy(column);
            setProductSortDir('asc');
        } else if (productSortDir === 'asc') {
            setProductSortDir('desc');
        } else {
            setProductSortBy(null);
            setProductSortDir(null);
        }
    };

    const cycleTransactionSort = (column) => {
        if (transactionSortBy !== column) {
            setTransactionSortBy(column);
            setTransactionSortDir('asc');
        } else if (transactionSortDir === 'asc') {
            setTransactionSortDir('desc');
        } else {
            setTransactionSortBy(null);
            setTransactionSortDir(null);
        }
    };

    // Sort cycle for columns where the data is ALREADY sorted ascending by default
    // (Product Desc and Interest Type Desc — the backend OrderBy clause sorts them
    // that way out of the gate). For these, an "asc" click is a no-op visually, so
    // we skip it: off → desc → off. One click reverses the default order; second
    // click restores it.
    const cycleProductSortFromAsc = (column) => {
        if (productSortBy === column && productSortDir === 'desc') {
            setProductSortBy(null);
            setProductSortDir(null);
        } else {
            setProductSortBy(column);
            setProductSortDir('desc');
        }
    };

    const cycleTransactionSortFromAsc = (column) => {
        if (transactionSortBy === column && transactionSortDir === 'desc') {
            setTransactionSortBy(null);
            setTransactionSortDir(null);
        } else {
            setTransactionSortBy(column);
            setTransactionSortDir('desc');
        }
    };

    // Icon picker for sortable column headers. By default: neutral icon when inactive,
    // up arrow for asc, down arrow for desc. For default-asc columns (Product Desc /
    // Interest Type Desc), pass `defaultAsc: true` so the inactive state shows an
    // up-arrow — communicating "data is currently ascending" — instead of the neutral
    // icon which would imply no order at all.
    const sortIconFor = (activeColumn, activeDir, column, opts) => {
        if (activeColumn === column) {
            return activeDir === 'asc' ? faArrowUpLong : faArrowDownLong;
        }
        if (opts && opts.defaultAsc) return faArrowUpLong;
        return faSort;
    };

    // Stable case-insensitive string compare for sorting (null/undefined → empty).
    const cmpStr = (a, b) => {
        const av = (a == null ? '' : String(a)).toLowerCase();
        const bv = (b == null ? '' : String(b)).toLowerCase();
        return av < bv ? -1 : av > bv ? 1 : 0;
    };

    // Map REV line type code to a displayable label so the dropdown shows REV/TAX/DED/NET
    // (matching the inline cells) instead of the raw single-letter code.
    const revLineTypeLabel = (code) => {
        switch (code) {
            case 'R': return 'REV';
            case 'T': return 'TAX';
            case 'D': return 'DED';
            case 'N': return 'NET';
            default: return '';
        }
    };

    // Shared filter-input styles so the inputs on the left (Products pane) and the
    // inputs on the right (Transactions pane) always render at the exact same height
    // and visual weight.
    //
    // minHeight is critical: Bootstrap's .form-control-sm class sets
    //   min-height: calc(1.5em + .5rem + 2px)
    // which silently OVERRIDES a plain `height: 24px` (min-height wins over height
    // when min-height > height). Without explicitly clamping minHeight, the search
    // boxes render at ~31px while the dropdowns render at 24px, making the two
    // panes visually mismatched. Setting minHeight: '24px' brings them in line.
    const filterInputStyle = { fontSize: '.8rem', paddingTop: '0px', paddingBottom: '0px', paddingLeft: '6px', paddingRight: '6px', height: '24px', minHeight: '24px', lineHeight: 1.2, boxSizing: 'border-box' };
    const filterSelectStyle = { fontSize: '.8rem', paddingTop: '0px', paddingBottom: '0px', paddingLeft: '6px', paddingRight: '22px', height: '24px', minHeight: '24px', lineHeight: 1.2, backgroundPosition: 'right 4px center', boxSizing: 'border-box', flex: 1 };
    const filterSortIconStyle = (active) => ({ cursor: 'pointer', width: '12px', height: '12px', color: active ? COLORS.gold : '#9ca3af', flexShrink: 0 });
    // Outer <li> styling for the sticky filter row. Mirrors the data <li>'s
    // marginLeft / paddingLeft trick so the columns line up, and pins itself
    // to the top of the scrollable <ul>.
    const filterRowLiStyle = { paddingLeft: '20px', marginLeft: '-42px', marginRight: '0px', fontSize: props.embedded ? '.75rem' : '.85rem', position: 'sticky', top: 0, zIndex: 2, background: '#ffffff', borderBottom: '1px solid #e5e7eb', paddingTop: '4px', paddingBottom: '4px' };

    // Master pane — products visible after filter + sort. Carries _originalIndex so
    // click handlers can still reference the position in the original productCodes
    // array (which all the existing change handlers and selection logic rely on).
    //
    // Mid-edit bypass: rows currently dirty (isDirty === true) stay visible regardless
    // of the filter. Without this, mid-edit values like "2" while typing "20" → "21"
    // would cause the row to disappear, stranding the user. We use isDirty (not
    // isReadOnly), because isReadOnly stays false after save for rows that now have
    // an override row — using it would permanently exempt overridden rows from the
    // filter. isDirty gets reset to false on save, so the row re-filters normally
    // once the change is persisted.
    const visibleProducts = useMemo(() => {
        let list = (productCodes || []).map((p, originalIndex) => ({ ...p, _originalIndex: originalIndex }));

        //productDesc isn't editable, so this filter never strands an in-progress edit
        if (productDescFilter) {
            const q = productDescFilter.toLowerCase();
            list = list.filter(p => (p.productDesc || '').toLowerCase().includes(q));
        }
        //cdexProductCode IS editable — keep the row visible only while it's dirty
        if (cdexProductCodeFilter !== '') {
            list = list.filter(p =>
                (p.cdexProductCode || '') === cdexProductCodeFilter
                || p.isDirty === true
            );
        }
        if (productSortBy && productSortDir) {
            list = [...list].sort((a, b) => {
                const r = cmpStr(a[productSortBy], b[productSortBy]);
                return productSortDir === 'asc' ? r : -r;
            });
        }
        return list;
    }, [productCodes, productDescFilter, cdexProductCodeFilter, productSortBy, productSortDir]);

    // Keep the selected product in sync with what's currently visible. When the
    // master-pane filters narrow visibleProducts so the currently selected product
    // is no longer in the list, snap selection to the first visible row — otherwise
    // the detail pane keeps showing transactions for a product the user can't see,
    // which produces "blank transaction" rows in the XLS export and generally
    // confuses the user about what they're looking at.
    useEffect(() => {
        if (!visibleProducts || visibleProducts.length === 0) return;
        if (selectedProductIndex == null) return;

        //is the currently selected product still visible after the filters?
        const stillVisible = visibleProducts.some(p => p._originalIndex === selectedProductIndex);
        if (stillVisible) return;

        //snap to the first visible product
        const firstVisible = visibleProducts[0];
        setSelectedProductIndex(firstVisible._originalIndex);
        setSelectedProduct(productCodes[firstVisible._originalIndex]);
    }, [visibleProducts]);

    // Distinct CDEX product codes from the loaded master list — used to populate the
    // CDEX Product Code column dropdown. Sorted alphabetically; '' first (blank means
    // "unmapped" in this UI).
    const distinctCdexProductCodes = useMemo(() => {
        const set = new Set();
        (productCodes || []).forEach(p => set.add(p.cdexProductCode || ''));
        return Array.from(set).sort();
    }, [productCodes]);

    // Detail pane — transactions visible for the selected product after filter + sort.
    // Same mid-edit bypass as the master pane: filters that target editable columns
    // (CdexTransactionCode, RevLineTypeCode) keep dirty rows visible so an in-progress
    // value change doesn't make the row vanish. Uses isDirty (not isReadOnly) so the
    // row re-filters normally after save — see note on visibleProducts above.
    const visibleTransactions = useMemo(() => {
        const source = (selectedProduct && selectedProduct.cdexTransactionRowDTO && selectedProduct.cdexTransactionRowDTO.data) || [];
        let list = source.map((t, originalIndex) => ({ ...t, _originalIndex: originalIndex }));

        //transactionDesc isn't editable — straightforward filter
        if (transactionDescFilter) {
            const q = transactionDescFilter.toLowerCase();
            list = list.filter(t => (t.transactionDesc || '').toLowerCase().includes(q));
        }
        //cdexTransactionCode IS editable — keep dirty rows visible
        if (cdexTransactionCodeFilter !== '') {
            list = list.filter(t =>
                (t.cdexTransactionCode || '') === cdexTransactionCodeFilter
                || t.isDirty === true
            );
        }
        //revLineTypeCode IS editable — keep dirty rows visible
        if (revLineTypeFilter !== '') {
            list = list.filter(t =>
                (t.revLineTypeCode || '') === revLineTypeFilter
                || t.isDirty === true
            );
        }
        if (transactionSortBy && transactionSortDir) {
            list = [...list].sort((a, b) => {
                const r = cmpStr(a[transactionSortBy], b[transactionSortBy]);
                return transactionSortDir === 'asc' ? r : -r;
            });
        }
        return list;
    }, [selectedProduct, transactionDescFilter, cdexTransactionCodeFilter, revLineTypeFilter, transactionSortBy, transactionSortDir]);

    const distinctCdexTransactionCodes = useMemo(() => {
        const data = (selectedProduct && selectedProduct.cdexTransactionRowDTO && selectedProduct.cdexTransactionRowDTO.data) || [];
        const set = new Set();
        data.forEach(t => set.add(t.cdexTransactionCode || ''));
        return Array.from(set).sort();
    }, [selectedProduct]);

    const distinctRevLineTypeCodes = useMemo(() => {
        const data = (selectedProduct && selectedProduct.cdexTransactionRowDTO && selectedProduct.cdexTransactionRowDTO.data) || [];
        const set = new Set();
        data.forEach(t => set.add(t.revLineTypeCode || ''));
        return Array.from(set).sort();
    }, [selectedProduct]);

    //render master pane (productCodes list)
    const showDataMaster = () => {
        return (
            <>
                <ul style={{ listStyleType: "none", height: props.embedded ? "390px" : "450px", marginRight: '12px', overflowY: 'auto', overflowX: 'hidden' }}>
                    {/* Filter / sort row — lives inside the same <ul> as the data so it
                        inherits the same horizontal offset (negative-margin trick on the
                        <li>) and therefore aligns perfectly with the data columns below.
                        The <ul> has overflowY: auto, which makes it the scrolling
                        container; position: sticky on this <li> then pins it at the
                        top of the visible <ul> region as the list scrolls. */}
                    <li className="dc-filter-row" style={filterRowLiStyle}>
                        <Row>
                            <Col xs={1}></Col>

                            {/* Product Desc — search box + sort cycle */}
                            <Col xs={6} style={{ paddingRight: '6px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <Form.Control
                                        type="text"
                                        size="sm"
                                        placeholder="Search..."
                                        value={productDescFilter}
                                        onChange={(e) => setProductDescFilter(e.target.value)}
                                        style={{ ...filterInputStyle, flex: 1 }}
                                    />
                                    <FontAwesomeIcon
                                        icon={sortIconFor(productSortBy, productSortDir, 'productDesc', { defaultAsc: true })}
                                        title={productSortBy === 'productDesc' && productSortDir === 'desc' ? 'Sorted descending — click to restore default (ascending)' : 'Currently ascending — click to sort descending'}
                                        style={filterSortIconStyle(productSortBy === 'productDesc')}
                                        onClick={() => cycleProductSortFromAsc('productDesc')}
                                    />
                                </div>
                            </Col>

                            {/* CDEX Product Code — dropdown + sort cycle */}
                            <Col xs={5}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <Form.Select
                                        size="sm"
                                        value={cdexProductCodeFilter}
                                        onChange={(e) => setCdexProductCodeFilter(e.target.value)}
                                        style={filterSelectStyle}
                                    >
                                        <option value="">All</option>
                                        {distinctCdexProductCodes.map((code, i) => (
                                            <option key={i} value={code}>{code === '' ? '(blank)' : code}</option>
                                        ))}
                                    </Form.Select>
                                    <FontAwesomeIcon
                                        icon={sortIconFor(productSortBy, productSortDir, 'cdexProductCode')}
                                        title={productSortBy === 'cdexProductCode' ? (productSortDir === 'asc' ? 'Sorted ascending — click for descending' : 'Sorted descending — click to clear') : 'Click to sort ascending'}
                                        style={filterSortIconStyle(productSortBy === 'cdexProductCode')}
                                        onClick={() => cycleProductSort('cdexProductCode')}
                                    />
                                </div>
                            </Col>
                        </Row>
                    </li>

                    {visibleProducts.length === 0 ? (
                        <li style={{ paddingLeft: '20px', marginLeft: '-42px', color: '#9ca3af', fontStyle: 'italic', fontSize: props.embedded ? '.8rem' : '.9rem' }}>
                            No products match the current filters.
                        </li>
                    ) : null}
                    {visibleProducts.map((cdexProductCode) => {
                        const index = cdexProductCode._originalIndex;
                        return (
                        <li key={index} ref={(index == 0 ? companyTopRef : null)}
                            style={{ cursor: 'pointer', paddingLeft: '20px', marginLeft: '-42px', marginRight: '0px', fontSize: props.embedded ? '.8rem' : '.9rem' }}
                            onMouseOver={() => handleMouseOver(index)}
                            onMouseLeave={() => handleMouseLeave(index)}
                            onClick={() => handleMouseClick(index)}>
                            <Row style={{
                                position: 'relative',
                                borderTop: hoveredIndex == index ? `2px solid ${COLORS.gold}` : '2px solid transparent',
                                borderBottom: hoveredIndex == index ? `2px solid ${COLORS.gold}` : '1px solid #e5e7eb',
                                backgroundColor: (selectedProductIndex == index ? '#f5f0e8' : (index % 2 === 0 ? '#fafbfc' : 'transparent')),
                                color: selectedProductIndex == index ? '#1f2937' : '#374151',
                                fontWeight: selectedProductIndex == index ? 600 : 400,
                                borderLeft: selectedProductIndex == index ? `8px solid ${COLORS.gold}` : '8px solid transparent',
                                paddingTop: '4px',
                                paddingBottom: '4px',
                                transition: 'border-color 120ms ease, background 120ms ease, color 120ms ease, transform 120ms ease, box-shadow 120ms ease',
                                ...(selectedProductIndex == index ? {
                                    boxShadow: '0 3px 8px rgba(0,0,0,0.18)',
                                    transform: 'translateX(3px)',
                                    zIndex: 2,
                                } : {}),
                            }} >
                                {/* Pencil icon — toggles isReadOnly so the row becomes editable */}
                                <Col xs={1} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingLeft: 0, paddingRight: 0 }}>
                                    {cdexProductCode.isReadOnly && (
                                        <FontAwesomeIcon
                                            icon={faPenToSquare}
                                            style={{ cursor: 'pointer', color: '#2563EB' }}
                                            onClick={(e) => { e.stopPropagation(); togglePencilProduct(index); }}
                                        />
                                    )}
                                </Col>
                                {/* Product Code column hidden for _dc — ProductCode same as ProductDesc */}
                                <Col xs={6} style={{ display: 'flex', alignItems: 'center' }}>
                                    <div style={{
                                        fontSize: props.embedded ? '.9rem' : '1.05rem',
                                        fontWeight: selectedProductIndex == index ? 700 : 600,
                                        letterSpacing: '.01em',
                                    }}>{cdexProductCode.productDesc}</div>
                                </Col>
                                <Col xs={5} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                        {(() => {
                                        if (!cdexProductCode.isReadOnly) {
                                                return <Form.Group className="mb-0" controlId="cdexProductCode">
                                                    <Form.Control name="cdexProductCode" size="sm" aria-label="CDEX Product Code" value={cdexProductCode.cdexProductCode} maxLength={2} style={{ width: '64px', fontSize: '.85rem', padding: '0px 6px', height: '24px', lineHeight: 1.2, textAlign: 'center', fontWeight: 600 }} onChange={e => handleProductChange(e)} />
                                                        </Form.Group>
                                            }
                                            else {
                                                return <div>{cdexProductCode.cdexProductCode}</div>;
                                            }
                                        })()}
                                </Col>
                            </Row>
                        </li>
                        );
                    })}
                </ul>
            </>
        );
    }

    //render detail pane (company display or edit)
    const showDataDetail = () => {
        return (
            <>
                {(() => {
                    if (isReadOnly) {
                        //return showDataDisplay()
                        return showDataEdit()
                    }
                    else {
                        //return showDataEdit()
                        return showDataDisplay()
                    }
                })()}
            </>
        );
    }

    //render company detail
    const showDataDisplay = () => {
        return (
            <>
                <Row>
                    <Col xs={8}>
                        <div style={{ fontWeight: '600', fontSize: '1.25rem', marginTop: '10px', color: '#1f2937' }} >Sender Detail</div>
                    </Col>
                    <Col xs={4} className="d-flex justify-content-end" style={{marginTop: '13px', color: '#374151'}} >
                        Company Code:&nbsp;&nbsp;{selectedProduct.companyCode}&nbsp;&nbsp;&nbsp;&nbsp;
                    </Col>
                </Row>
                <Form style={{ borderTop: '1px solid #e5e7eb', marginTop: '10px', paddingRight: '10px' }} />
                <Row style={{ paddingRight: '10px', paddingTop: '10px' }}>
                    <Col xs={2}>
                        Name:
                    </Col>
                    <Col xs={10}>
                        <div>
                            {selectedProduct.companyName}
                        </div>                        
                        <div>
                            {((selectedProduct.additionalNameInfo ?? '') == '' ? null : '(' + selectedProduct.additionalNameInfo + ')')}
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col xs={2}>
                        Address:
                    </Col>
                    <Col xs={10}>
                        <div>
                            {selectedProduct.address1} {selectedProduct.address2} {selectedProduct.address3}
                        </div>
                        <div>
                            {selectedProduct.city}, {selectedProduct.state} {selectedProduct.zip}
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col xs={2}>
                        Email:
                    </Col>
                    <Col xs={10}>
                        <div style={{ color: ((selectedProduct.email ?? '') == '' ? 'red' : 'black') }}>{(selectedProduct.email ?? '') == '' ? 'Not available' : selectedProduct.email}</div>
                    </Col>
                </Row>
                <Row>
                    <Col xs={2}>
                        Phone:
                    </Col>
                    <Col xs={10}>
                        <div style={{ color: ((selectedProduct.phone ?? '') == '' ? 'red' : 'black') }}>{(selectedProduct.phone ?? '') == '' ? 'Not available' : selectedProduct.phone}</div>
                    </Col>
                </Row>
                <Row>
                    <Col xs={2}>
                        Fax:
                    </Col>
                    <Col xs={10}>
                        <div style={{ color: ((selectedProduct.fax ?? '') == '' ? 'red' : 'black') }}>{(selectedProduct.fax ?? '') == '' ? 'Not available' : selectedProduct.fax}</div>
                    </Col>                    
                </Row>
                //<Row>
                //    <Col xs={2}>
                //        Tax Reg #:
                //    </Col>
                //    <Col xs={10}>
                //        <div style={{ color: ((selectedProduct.taxRegNumber ?? '') == '' ? 'red' : 'black') }}>{(selectedProduct.taxRegNumber ?? '') == '' ? 'Not available' : selectedProduct.taxRegNumber}</div>
                //    </Col>
                //</Row>
                <Row>
                    <Col xs={3}>
                        CDEX Sender Code:
                    </Col>
                    <Col xs={9}>
                        <div style={{ color: ((selectedProduct.cdexSenderCode ?? '') == '' ? 'red' : 'black') }}>{(selectedProduct.cdexSenderCode ?? '') == '' ? 'Not available' : selectedProduct.cdexSenderCode}</div>
                    </Col>
                </Row>
            </>
        );
    }

    //render detail pane (cdexTransactionCodes list)
    const showDataEdit = () => {
        return (
            <>
                {/* Parent-product header for the transactions pane. Static (the ul handles its own scroll).
                    Minimal style — small uppercase label + bold product name + gold CDEX chip. */}
                <div style={{
                    backgroundColor: '#ffffff',
                    borderBottom: `2px solid ${COLORS.gold}`,
                    padding: '6px 4px 8px 4px',
                    marginBottom: '4px',
                    color: '#6b7280',
                    letterSpacing: '.05em',
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    fontSize: props.embedded ? '.65rem' : '.7rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                }}>
                    <span>Transactions under</span>
                    <span style={{
                        color: '#1f2937',
                        textTransform: 'none',
                        fontSize: props.embedded ? '.85rem' : '.95rem',
                        fontWeight: 700,
                        letterSpacing: 'normal',
                    }}>
                        {selectedProduct.productDesc}
                    </span>
                    {selectedProduct.cdexProductCode ? (
                        <span style={{
                            padding: '1px 8px',
                            backgroundColor: COLORS.gold,
                            color: '#1f2937',
                            borderRadius: '10px',
                            fontSize: props.embedded ? '.7rem' : '.75rem',
                            fontWeight: 700,
                            letterSpacing: 'normal',
                            textTransform: 'none',
                        }}>
                            {selectedProduct.cdexProductCode}
                        </span>
                    ) : null}
                </div>
                {/* ul height reduced ~60px to make room for the header above so the
                    right pane's total height stays aligned with the master pane. */}
                <ul style={{ listStyleType: "none", height: props.embedded ? "330px" : "390px", marginRight: '12px', overflowY: 'auto', overflowX: 'hidden' }}>
                    {/* Filter / sort row — same sticky-first-li pattern as the master pane
                        so the inputs align with the data columns. */}
                    <li className="dc-filter-row" style={filterRowLiStyle}>
                        <Row>
                            <Col xs={1}></Col>

                            {/* Interest Type Desc (transactionDesc) — search box + sort cycle */}
                            <Col xs={3} style={{ paddingRight: '6px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <Form.Control
                                        type="text"
                                        size="sm"
                                        placeholder="Search..."
                                        value={transactionDescFilter}
                                        onChange={(e) => setTransactionDescFilter(e.target.value)}
                                        style={{ ...filterInputStyle, flex: 1 }}
                                    />
                                    <FontAwesomeIcon
                                        icon={sortIconFor(transactionSortBy, transactionSortDir, 'transactionDesc', { defaultAsc: true })}
                                        title={transactionSortBy === 'transactionDesc' && transactionSortDir === 'desc' ? 'Sorted descending — click to restore default (ascending)' : 'Currently ascending — click to sort descending'}
                                        style={filterSortIconStyle(transactionSortBy === 'transactionDesc')}
                                        onClick={() => cycleTransactionSortFromAsc('transactionDesc')}
                                    />
                                </div>
                            </Col>

                            {/* CDEX Transaction Code — dropdown + sort cycle */}
                            <Col xs={3} style={{ paddingRight: '6px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <Form.Select
                                        size="sm"
                                        value={cdexTransactionCodeFilter}
                                        onChange={(e) => setCdexTransactionCodeFilter(e.target.value)}
                                        style={filterSelectStyle}
                                    >
                                        <option value="">All</option>
                                        {distinctCdexTransactionCodes.map((code, i) => (
                                            <option key={i} value={code}>{code === '' ? '(blank)' : code}</option>
                                        ))}
                                    </Form.Select>
                                    <FontAwesomeIcon
                                        icon={sortIconFor(transactionSortBy, transactionSortDir, 'cdexTransactionCode')}
                                        title={transactionSortBy === 'cdexTransactionCode' ? (transactionSortDir === 'asc' ? 'Sorted ascending — click for descending' : 'Sorted descending — click to clear') : 'Click to sort ascending'}
                                        style={filterSortIconStyle(transactionSortBy === 'cdexTransactionCode')}
                                        onClick={() => cycleTransactionSort('cdexTransactionCode')}
                                    />
                                </div>
                            </Col>

                            {/* REV Line Type — dropdown + sort cycle */}
                            <Col xs={3} style={{ paddingRight: '6px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <Form.Select
                                        size="sm"
                                        value={revLineTypeFilter}
                                        onChange={(e) => setRevLineTypeFilter(e.target.value)}
                                        style={filterSelectStyle}
                                    >
                                        <option value="">All</option>
                                        {distinctRevLineTypeCodes.map((code, i) => (
                                            <option key={i} value={code}>{code === '' ? '(blank)' : (revLineTypeLabel(code) || code)}</option>
                                        ))}
                                    </Form.Select>
                                    <FontAwesomeIcon
                                        icon={sortIconFor(transactionSortBy, transactionSortDir, 'revLineTypeCode')}
                                        title={transactionSortBy === 'revLineTypeCode' ? (transactionSortDir === 'asc' ? 'Sorted ascending — click for descending' : 'Sorted descending — click to clear') : 'Click to sort ascending'}
                                        style={filterSortIconStyle(transactionSortBy === 'revLineTypeCode')}
                                        onClick={() => cycleTransactionSort('revLineTypeCode')}
                                    />
                                </div>
                            </Col>

                            {/* Apply to All — no filter / no sort, just an empty cell aligned
                                with the data row's apply checkbox column */}
                            <Col xs={2}></Col>
                        </Row>
                    </li>

                    {visibleTransactions.length === 0 ? (
                        <li style={{ paddingLeft: '20px', marginLeft: '-42px', color: '#9ca3af', fontStyle: 'italic', fontSize: props.embedded ? '.72rem' : '.8rem' }}>
                            No transactions match the current filters.
                        </li>
                    ) : null}
                    {visibleTransactions.map((cdexTransactionCode) => {
                        const index = cdexTransactionCode._originalIndex;
                        return (
                        <li key={index} ref={(index == 0 ? companyTopRef : null)}
                            onMouseOver={() => setHoveredTransactionIndex(index)}
                            onMouseLeave={() => setHoveredTransactionIndex(null)}
                            style={{ paddingLeft: '20px', marginLeft: '-42px', marginRight: '0px', fontSize: props.embedded ? '.72rem' : '.8rem' }}>
                            <Row style={{
                                borderTop: hoveredTransactionIndex == index ? `2px solid ${COLORS.gold}` : '2px solid transparent',
                                borderBottom: hoveredTransactionIndex == index ? `2px solid ${COLORS.gold}` : '1px solid #e5e7eb',
                                backgroundColor: index % 2 === 0 ? '#fafbfc' : 'transparent',
                                color: '#374151',
                                paddingTop: '4px',
                                paddingBottom: '4px',
                                transition: 'border-color 120ms ease'
                            }} >
                                {/* Pencil icon — toggles transaction isReadOnly */}
                                <Col xs={1} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingLeft: 0, paddingRight: 0 }}>
                                    {cdexTransactionCode.isReadOnly && (
                                        <FontAwesomeIcon
                                            icon={faPenToSquare}
                                            style={{ cursor: 'pointer', color: '#2563EB' }}
                                            onClick={() => togglePencilTransaction(index)}
                                        />
                                    )}
                                </Col>
                                {/* Interest Type Code column hidden for _dc */}
                                <Col xs={3} style={{ display: 'flex', alignItems: 'center' }}>
                                    <div>{cdexTransactionCode.transactionDesc}</div>
                                </Col>
                                <Col xs={3} >
                                    {(() => {
                                        if (!cdexTransactionCode.isReadOnly) {
                                            return <Form.Group className="mb-0" controlId="cdexTransactionCode">
                                                <Form.Control name="cdexTransactionCode" size="sm" aria-label="CDEX Transaction Code" value={cdexTransactionCode.cdexTransactionCode} style={{ width: '100%', fontSize: '.85rem', padding: '0px 6px', height: '24px', lineHeight: 1.2 }} onChange={(e) => handleTransactionChange(index, e)} />
                                            </Form.Group>
                                        }
                                        else {
                                            return <div>{cdexTransactionCode.cdexTransactionCode}</div>;
                                        }
                                    })()}
                                </Col>
                                <Col xs={3} >
                                    {(() => {
                                        if (!cdexTransactionCode.isReadOnly) {
                                            return <Form.Group className="mb-0" controlId="companyCode">
                                                <Form.Select name="revLineTypeCode" size="sm" aria-label="REV Line Type" value={cdexTransactionCode.revLineTypeCode} style={{ fontSize: '.85rem', padding: '0px 22px 0px 6px', height: '24px', lineHeight: 1.2, backgroundPosition: 'right 4px center' }} onChange={(e) => handleTransactionChange(index, e)} >
                                                    {revLineTypes.map((type, i) => (
                                                        <option key={i} value={type.code}>{type.value}</option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        }
                                        else {
                                            switch (cdexTransactionCode.revLineTypeCode) {
                                                case 'R':
                                                    return <div>REV</div>
                                                case 'T':
                                                    return <div>TAX</div>
                                                case 'D':
                                                    return <div>DED</div>
                                                case 'N':
                                                    return <div>NET</div>
                                                default:
                                                    return <div></div>
                                            }
                                        }
                                    })()}
                                </Col>
                                {/* Apply-to-all column — checkbox + inline label so it's
                                    obvious what the checkbox does. Only renders for the row
                                    that's currently being edited; read-only rows leave the
                                    cell empty. */}
                                <Col xs={2} style={{ display: 'flex', alignItems: 'center' }}>
                                    {!cdexTransactionCode.isReadOnly && (
                                        <Form.Check
                                            type="checkbox"
                                            id={`applyToAll-${index}`}
                                            label="Apply to all products"
                                            title="Apply this change to every product with this transaction"
                                            checked={!!cdexTransactionCode.applyToAll}
                                            onChange={(e) => handleTransactionApplyToAllChange(index, e.target.checked)}
                                            style={{ fontSize: '.68rem', color: '#374151', margin: 0, lineHeight: 1.15 }}
                                        />
                                    )}
                                </Col>
                            </Row>
                        </li>
                        );
                    })}
                </ul>
            </>
        );
    }

    //pagination example
    //https://github.com/lukaaspl/ellipsis-pagination/blob/master/src/components/Pagination.js

    //render input criteria
    const showInputCriteria = () => {
        return (
            <Container fluid>
                <Row>
                    <Col xs={5}>
                        <Button
                            size='sm'
                            style={{ ...btnGold, minWidth: props.embedded ? '160px' : '200px', padding: props.embedded ? '2px 12px' : undefined, fontSize: props.embedded ? '.8rem' : undefined, whiteSpace: 'nowrap' }}
                            onMouseEnter={e => Object.assign(e.target.style, boxshadowHover)}
                            onMouseLeave={e => Object.assign(e.target.style, boxshadowUnHover(btnGold))}
                            disabled={!anyProductDirty}
                            onClick={anyProductDirty ? () => updateProducts() : null}
                        >
                            Map Product Codes
                        </Button>
                    </Col>
                    <Col xs={7}>
                        <div className="d-flex align-items-center">
                            <Button
                                size='sm'
                                style={{ ...btnGold, minWidth: props.embedded ? '160px' : '200px', padding: props.embedded ? '2px 12px' : undefined, fontSize: props.embedded ? '.8rem' : undefined, whiteSpace: 'nowrap' }}
                                onMouseEnter={e => Object.assign(e.target.style, boxshadowHover)}
                                onMouseLeave={e => Object.assign(e.target.style, boxshadowUnHover(btnGold))}
                                disabled={!anyTransactionDirty}
                                onClick={anyTransactionDirty ? () => updateTransactions() : null}
                            >
                                Map Transaction Codes
                            </Button>
                            {/* XLS download — right-aligned via ms-auto. Mirrors the
                                AdminDashboard pattern: green excel icon, spinner while
                                the download is in flight. */}
                            <div className="ms-auto">
                                {downloadDataStatus === 'loading' ? (
                                    <Button size='sm' variant="primary" style={{ backgroundColor: 'green', borderColor: 'green' }}>
                                        <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" />
                                    </Button>
                                ) : (
                                    /* Custom CSS tooltip — see the .dc-inline-tip rule in
                                       the <style> block above. Shows instantly on hover
                                       (no native-title delay) and uses position:absolute
                                       with right:calc(100% + 8px) so it appears to the
                                       LEFT of the button, well inside the page content
                                       area — no scrollbar flash. */
                                    <span className="dc-inline-tip">
                                        <Button
                                            size='sm'
                                            variant="primary"
                                            onClick={handleExportXls}
                                            style={{ backgroundColor: 'green', borderColor: 'green' }}
                                        >
                                            <FontAwesomeIcon icon={faFileExcel} />
                                        </Button>
                                        <span className="dc-inline-tip-text">Download CDEX Override as XLS</span>
                                    </span>
                                )}
                            </div>
                        </div>
                    </Col>
                </Row>
                <Row style={{fontSize: props.embedded ? '.8rem' : '.9rem', marginTop: props.embedded ? '8px' : '20px', color: '#f1f1f1', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em'}} >
                    <Col xs={5}>
                        <Row>
                            <Col xs={1}></Col>
                            <Col xs={6}>Product Desc</Col>
                            <Col xs={5} style={{ textAlign: 'center' }}>CDEX Product Code</Col>
                        </Row>
                    </Col>
                    <Col xs={7} style={{borderLeft: '1px solid #e5e7eb'}} >
                        <Row>
                            <Col xs={1}></Col>
                            <Col xs={3}>Interest Type Desc</Col>
                            <Col xs={3}>CDEX Transaction Code</Col>
                            <Col xs={3}>REV Line Type</Col>
                            {/* Apply-to-all column header intentionally blank — the inline
                                checkbox label tells the story when the row is in edit mode. */}
                            <Col xs={2}></Col>
                        </Row>
                    </Col>
                </Row>

            </Container>
        );
    }

    return (
        <div>
            {/* Force a consistent 24px height on every input/select inside the sticky
                filter row. Bootstrap's .form-control-sm / .form-select-sm classes set a
                min-height that wins over inline `height` even when min-height is also
                set inline (due to browser quirks around shorthand resolution and
                cascading sm-size rules). !important guarantees we win. */}
            <style>{`
                .dc-filter-row .form-control-sm,
                .dc-filter-row .form-select-sm {
                    height: 24px !important;
                    min-height: 24px !important;
                    max-height: 24px !important;
                    padding-top: 0 !important;
                    padding-bottom: 0 !important;
                    line-height: 1.2 !important;
                    box-sizing: border-box !important;
                }
                /* Instant CSS-only tooltip for the XLS button. Native HTML title
                   has a ~500ms hover delay; Popper-backed tooltips were causing a
                   brief page-level scrollbar flash on first render. A pure-CSS
                   bubble shown via :hover sidesteps both. */
                .dc-inline-tip { position: relative; display: inline-block; }
                .dc-inline-tip > .dc-inline-tip-text {
                    position: absolute;
                    right: calc(100% + 8px);   /* sit to the LEFT of the button */
                    top: 50%;
                    transform: translateY(-50%);
                    background: #1f2937;
                    color: #f9fafb;
                    padding: 4px 8px;
                    border-radius: 4px;
                    font-size: .75rem;
                    line-height: 1.2;
                    white-space: nowrap;
                    pointer-events: none;
                    opacity: 0;
                    visibility: hidden;
                    z-index: 5;
                }
                .dc-inline-tip:hover > .dc-inline-tip-text {
                    opacity: 1;
                    visibility: visible;
                }
            `}</style>
            {!props.embedded && (
                <Row className="align-items-center" style={{ margin: '0 0 4px 0' }}>
                    <Col xs={1} style={{ marginLeft: '-7px' }}>
                        <span style={{ ...backBtn }} onClick={() => navigate('/settings')}>
                            <FontAwesomeIcon icon={faArrowLeft} />
                        </span>
                    </Col>
                    <Col xs={10}>
                        <p style={{ fontSize: '1.75rem', fontWeight: '700', color: '#333333', margin: 0, textAlign: 'center' }}>CDEX Account Override</p>
                    </Col>
                    <Col xs={1}></Col>
                </Row>
            )}
            {(() => {
                switch (pageStatus) {
                    case 'loading':
                        return showPageLoading()
                    case 'ready':
                        return showTableContainer()
                    case 'error':
                        return showPageError()
                    default:
                        return <p>No Page Loaded.</p>
                }
            })()}
        </div>
    );
}

export default CdexCodesOverride_dc;
